import * as Sentry from '@sentry/sveltekit';
import * as DeviceId from '$lib/utils/device-id.js';
import {
	enableEventDeferral,
	flushDeferredEvents,
	disableEventDeferral,
	deferredStatelessEventLog,
} from '$lib/utils/device-id.js';
import { app, setGlobalStore } from '$lib/stores/app/store.js';
import { setMeta } from '$lib/stores/app/actions.js';
import Cookies from 'js-cookie';
import { AnalyticsService } from '$lib/services/analytics/index.js';
import { qualifyExperiments } from '$lib/services/experiments/index.js';
import { experimentService } from '$lib/services/experiments/client/index.js';
import { ProductExperienceService } from '$lib/services/product-experience/index.js';
import { validateUser, tokenIntrospect } from '$lib/stores/user/action.js';
import {
	PAGES,
	QUERY_PARAM_KEYS,
	SENTRY_ERROR_LEVELS,
	LINK_FROM_APP_PLATFORMS,
	COOKIES,
} from '$lib/utils/constants.js';
import { getEnvironment } from '$lib/utils/environment';
import { get } from 'svelte/store';
import { ApiService } from '$lib/services/api/index.js';

/**
 * App Initialization Service
 *
 * Manages the complete app initialization flow including device ID setup,
 * authentication (token introspection and user validation), meta initialization,
 * experiments, and product experience configuration.
 *
 * Also provides a mechanism to ensure hooks.client.js initialization completes
 * before route policies and other client-side code execute, solving the race
 * condition where route load functions may execute before hooks.client.js init()
 * completes.
 */
export class AppInitializationService {
	// Private state for initialization promise management
	private static initializationPromise: Promise<void> | null = null;
	private static initializationResolve: (() => void) | null = null;
	private static isInitialized = false;

	/**
	 * Creates and returns a promise that resolves when app initialization completes.
	 * Multiple calls return the same promise instance.
	 * @returns {Promise<void>} Promise that resolves when initialization is complete
	 */
	public static waitForInitialization(): Promise<void> {
		if (this.isInitialized) {
			return Promise.resolve();
		}

		if (!this.initializationPromise) {
			this.initializationPromise = new Promise<void>((resolve) => {
				this.initializationResolve = resolve;
			});
		}

		return this.initializationPromise;
	}

	/**
	 * Marks app initialization as complete.
	 * This should be called from hooks.client.js after all initialization completes.
	 * @returns {void}
	 */
	public static markInitializationComplete(): void {
		if (this.isInitialized) {
			// Already initialized, ignore duplicate calls
			return;
		}

		this.isInitialized = true;

		if (this.initializationResolve) {
			this.initializationResolve();
			this.initializationResolve = null;
			this.initializationPromise = null;
		}
	}

	/**
	 * Resets the initialization state (useful for testing).
	 * @returns {void}
	 */
	public static resetInitialization(): void {
		this.isInitialized = false;
		this.initializationPromise = null;
		this.initializationResolve = null;
	}

	/**
	 * Main initialization method that runs all app initialization logic.
	 * This should be called from hooks.client.js init() function.
	 * @returns {Promise<void>}
	 */
	public static async initialize(): Promise<void> {
		const params = new URLSearchParams(window.location.search);
		// Enable event deferral before device ID initialization to ensure validateUser fires first
		// TODO: remove this once we have refactored the device id logic away from cookies - AD
		enableEventDeferral();
		const { deviceId, isFirstTimeVisit } = await this.initializeDeviceId();

		// Ensure product experience is set from URL
		await ProductExperienceService.setProductExperienceFromQuery(false);

		await this.initializeMeta(params, deviceId);
		await this.initializeGlobalStore(params, isFirstTimeVisit);

		await this.handleTokenIntrospection();

		const plogToken = await this.handlePlogSessionUpgrade();
		await this.handleValidateUser(plogToken);

		await this.initializeExperiments();

		// Mark initialization complete after all initialization logic finishes
		// This ensures route policies wait for global initialization (including validateUser, setMeta, experiments, etc.) before executing
		this.markInitializationComplete();
	}

	/**
	 * Initializes device ID and returns device ID along with first-time visit status.
	 * Handles migration, initialization, error handling, and fallback device ID generation.
	 * @returns {Promise<{ deviceId: string; isFirstTimeVisit: boolean }>}
	 */
	private static async initializeDeviceId(): Promise<{
		deviceId: string;
		isFirstTimeVisit: boolean;
	}> {
		DeviceId.migrateDeviceIdAndFixErroneousValues();

		const isFirstTimeVisit = !DeviceId.getDeviceId();
		const isDeviceIdInitialized = await DeviceId.initializeDeviceId();

		if (!isDeviceIdInitialized) {
			DeviceId.fireDeviceIdFailureEvent(DeviceId.DEVICE_ID_FAILURE_TYPES.init_failure);
		}

		let deviceId = DeviceId.getDeviceId();

		if (!deviceId) {
			DeviceId.fireDeviceIdFailureEvent(DeviceId.DEVICE_ID_FAILURE_TYPES.get_failure);
			deviceId = 'client_failure:on_app_init:hooks_client.js';
		}

		return { deviceId, isFirstTimeVisit };
	}

	/**
	 * Handles token introspection from URL params.
	 * Performs token introspection from URL params (TOKEN, USER_TOKEN, or PAYLOAD).
	 * Skips token introspection for password-reset route (uses recovery token, not auth token).
	 * @returns {Promise<void>}
	 */
	private static async handleTokenIntrospection(): Promise<void> {
		const url = new URL(window.location.href);
		const isInboundGateway = url.pathname.includes(PAGES.INTERNAL.INBOUND_GATEWAY);
		const isPasswordReset = url.pathname.includes(PAGES.INTERNAL.RESET_PASSWORD);
		const isMagicLinkRedeem = url.pathname.includes(PAGES.INTERNAL.UNIVERSAL.MAGIC_LINK_REDEEM);

		// Skip token introspection for password-reset route (uses recovery token, not auth token)
		if (!isPasswordReset && !isMagicLinkRedeem) {
			// Centralized token introspection for all routes
			// Handles three token sources: TOKEN, USER_TOKEN, and PAYLOAD (for inbound-gateway)
			try {
				await this.introspectTokenFromUrl(isInboundGateway);
			} catch (error) {
				Sentry.captureMessage('AppInit: Token introspection failed', {
					level: SENTRY_ERROR_LEVELS.FATAL,
					extra: {
						error: error,
					},
				});
			}
		}
	}

	/**
	 * Handles plog session upgrade for reactivation route.
	 * Fixes issue where 1.0 inactive Unity users are redirected from the student homepage to the reactivation page with an unauthenticated webview session and getting kicked out to the home page.
	 * @returns {Promise<string | null>} The JWT token if successful, null otherwise
	 */
	private static async handlePlogSessionUpgrade(): Promise<string | null> {
		await deferredStatelessEventLog(
			{
				event_subtype: 'handlePlogSessionUpgrade',
				href: window.location.href,
				cookies: Cookies.get(),
				documentCookies: document.cookie,
				appMeta: get(app)?.meta,
				deviceId: DeviceId.getDeviceId(),
			},
			DeviceId.getDeviceId() ?? '',
			true
		);
		// Bug where 1.0 inactive Unity users are redirected from the student homepage to the reactivation page with an unauthenticated webview session and getting kicked out to the home page.
		if (get(app)?.meta?.inApp && window.location.href.includes(PAGES.INTERNAL.REACTIVATION)) {
			Sentry.captureMessage('AppInit: plog session upgrade cookies', {
				level: SENTRY_ERROR_LEVELS.INFO,
				extra: {
					href: window.location.href,
					cookies: Cookies.get(),
					documentCookies: document.cookie,
				},
			});

			if (!Cookies.get(COOKIES.PLOG)) {
				Sentry.captureMessage('AppInit: plog cookie not found', {
					level: SENTRY_ERROR_LEVELS.FATAL,
				});
				return null;
			}

			try {
				const response = await ApiService.upgradeSession();
				// @ts-expect-error - response type is not fully typed, but contains token property
				return response?.token || null;
			} catch (error) {
				Sentry.captureMessage('AppInit: plog session upgrade failed', {
					level: SENTRY_ERROR_LEVELS.FATAL,
					extra: {
						error: error,
					},
				});
			}
		}
		return null;
	}

	/**
	 * Handles user validation.
	 * Validates user state and flushes deferred device ID events.
	 * @param {string | null} token - Optional token from plog session upgrade to pass to validateUser
	 * @returns {Promise<void>}
	 */
	private static async handleValidateUser(token: string | null): Promise<void> {
		const url = new URL(window.location.href);

		// Only set preserveSessionDeviceId to false if deviceId query param exists
		const hasDeviceIdQueryParam = url.searchParams.has(QUERY_PARAM_KEYS.DEVICE_ID);
		/** @type {{ preserveSessionDeviceId?: boolean; token?: string }} */
		const validateUserOptions: { preserveSessionDeviceId?: boolean; token?: string } = {};

		if (hasDeviceIdQueryParam) {
			validateUserOptions.preserveSessionDeviceId = false;
		}

		// If plog session upgrade returned a token, pass it to validateUser
		if (token) {
			validateUserOptions.token = token;
		}

		try {
			// @ts-expect-error - validateUser accepts options with optional preserveSessionDeviceId
			await validateUser(validateUserOptions);
		} catch (error) {
			Sentry.captureMessage('AppInit: User validation failed', {
				level: SENTRY_ERROR_LEVELS.FATAL,
				extra: {
					error: error,
				},
			});
		}

		// Flush deferred device ID events after validateUser completes
		try {
			await flushDeferredEvents();
		} catch (error) {
			Sentry.captureMessage('AppInit: Failed to flush deferred device ID events', {
				extra: {
					error: error,
				},
			});
		}
		disableEventDeferral();
	}

	/**
	 * Checks if the link is from an app (Unity or Flutter) by checking query params and inbound gateway payload.
	 * For inbound-gateway route, parses payload early to set isLinkFromApp before experiments initialize.
	 * @param {URLSearchParams} params - URL search parameters
	 * @returns {Promise<boolean>} True if link is from any app (Unity iOS or Flutter iOS/Android), false otherwise
	 */
	private static async isLinkFromApp(params: URLSearchParams): Promise<boolean> {
		// Check for Unity app (via LINK_FROM_APP_PLATFORM query param)
		const isLinkFromUnityApp =
			get(app).global.linkFromAppPlatform === LINK_FROM_APP_PLATFORMS.IOS ||
			params.get(QUERY_PARAM_KEYS.LINK_FROM_APP_PLATFORM) === LINK_FROM_APP_PLATFORMS.IOS;

		if (isLinkFromUnityApp) {
			return true;
		}

		// Check for Flutter app (via inbound gateway payload)
		const url = new URL(window.location.href);
		const isInboundGateway = url.pathname.includes(PAGES.INTERNAL.INBOUND_GATEWAY);

		if (!isInboundGateway) {
			return false;
		}

		const payload = params.get(QUERY_PARAM_KEYS.PAYLOAD);

		try {
			const { validateInboundData } = await import('$lib/services/inbound-gateway/validation');
			const validatedData = await validateInboundData(payload);
			if (validatedData?.platform) {
				const isLinkFromFlutterApp =
					validatedData.platform === LINK_FROM_APP_PLATFORMS.IOS ||
					validatedData.platform === LINK_FROM_APP_PLATFORMS.ANDROID;

				return isLinkFromFlutterApp;
			}
		} catch (error) {
			// If payload validation fails, experiments will use default, but log as fatal error
			Sentry.captureMessage('AppInit: Failed to parse inbound gateway payload', {
				level: SENTRY_ERROR_LEVELS.FATAL,
				extra: {
					error: error,
				},
			});
		}

		return false;
	}

	/**
	 * Initializes meta values (including inApp detection) BEFORE route conditions run.
	 * Route conditions depend on config.meta.inApp, so this must happen early.
	 * @param {URLSearchParams} params - URL search parameters
	 * @param {string} deviceId - Device ID
	 * @returns {Promise<void>}
	 */
	private static async initializeMeta(params: URLSearchParams, deviceId: string): Promise<void> {
		const isLinkFromEmail = params.get(QUERY_PARAM_KEYS.EMAIL_CLICK) ? 'true' : 'false';

		const isLinkFromApp = await this.isLinkFromApp(params);

		try {
			await setMeta({
				...Cookies.get(),
				acquisitionSource: AnalyticsService.getSourceTag() ?? '',
				deviceId,
				isLinkFromEmail: isLinkFromEmail,
				isLinkFromApp: get(app).meta.isLinkFromApp ? true : isLinkFromApp,
			});
		} catch (error) {
			Sentry.captureMessage('AppInit: Failed to set meta values', {
				level: SENTRY_ERROR_LEVELS.FATAL,
				extra: {
					error: error,
				},
			});
		}
	}

	/**
	 * Initializes global store with app-level state.
	 * @param {URLSearchParams} params - URL search parameters
	 * @param {boolean} isFirstTimeVisit - Whether this is a first-time visit
	 * @returns {Promise<void>}
	 */
	private static async initializeGlobalStore(
		params: URLSearchParams,
		isFirstTimeVisit: boolean
	): Promise<void> {
		const linkFromAppPlatform = params.get(QUERY_PARAM_KEYS.LINK_FROM_APP_PLATFORM);

		await setGlobalStore({
			...(linkFromAppPlatform && { linkFromAppPlatform }),
			isFirstTimeVisit,
			environment: getEnvironment(),
		});
	}

	/**
	 * Initializes experiments
	 * @returns {Promise<void>}
	 */
	private static async initializeExperiments(): Promise<void> {
		await qualifyExperiments();
		await experimentService.initializeExperiments();
	}

	/**
	 * Centralized token introspection for app initialization.
	 * Handles three token sources:
	 * 1. QUERY_PARAM_KEYS.TOKEN - standard token param
	 * 3. QUERY_PARAM_KEYS.PAYLOAD - payload param (for inbound-gateway route)
	 * @param {boolean} isInboundGateway - Whether the current route is inbound-gateway
	 * @returns {Promise<void>}
	 */
	private static async introspectTokenFromUrl(isInboundGateway: boolean): Promise<void> {
		const url = new URL(window.location.href);

		// Check for standard TOKEN param first
		let token = url.searchParams.get(QUERY_PARAM_KEYS.TOKEN);
		const source = url.searchParams.get(QUERY_PARAM_KEYS.SOURCE);

		// Check for PAYLOAD param (used by inbound-gateway)
		if (!token && isInboundGateway) {
			const payload = url.searchParams.get(QUERY_PARAM_KEYS.PAYLOAD);
			if (payload) {
				const { validateInboundData } = await import('$lib/services/inbound-gateway/validation');
				const validatedData = await validateInboundData(payload);
				if (validatedData?.token) {
					token = validatedData.token;
				}
			}
		}

		if (!token) {
			return;
		}

		// Introspect the token
		await tokenIntrospect(token, source);
	}
}
